import apiService from './api'
import { buildEndpoint } from '../utils/apiUtils'
import { logError, log } from '../utils/logger'

export interface PaymobPaymentLinkResponse {
    success: boolean
    paymentLink?: string
    message?: string
    error?: string
}

/**
 * Create Paymob payment link for an order
 * Uses buildEndpoint to automatically select correct endpoint based on user type
 * @param orderId - The order ID to create payment link for
 * @returns Payment link response
 */
export async function createPaymobPaymentLink(
    orderId: string | number
): Promise<PaymobPaymentLinkResponse> {
    try {
        const formData = new FormData()
        formData.append('order_id', orderId.toString())

        // Use buildEndpoint to get the correct endpoint based on user type
        // For cashier: /cashier/paymob/createPaymentLink
        // For CS: /customerService/paymob/createPaymentLink
        const endpoint = buildEndpoint('/cashier/paymob/createPaymentLink')

        log('Creating Paymob payment link for order:', orderId, 'using endpoint:', endpoint)

        const response = await apiService.axios.post(
            endpoint,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 15000, // 15 seconds timeout
            }
        )

        if (response.status === 200 && response.data) {
            const paymentLink = response.data.paymentLink ||
                response.data.payment_link ||
                response.data.link ||
                response.data.data?.paymentLink ||
                response.data.data?.payment_link ||
                response.data.data?.link

            return {
                success: true,
                paymentLink: paymentLink,
                message: response.data.message,
            }
        } else {
            return {
                success: false,
                error: 'Failed to create payment link',
            }
        }
    } catch (error: any) {
        logError('Paymob payment link creation error:', error)

        // Extract detailed error information
        const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to create payment link'

        return {
            success: false,
            error: errorMessage,
        }
    }
}

/**
 * Create Paymob payment link in background without opening a new tab
 * This function sends the request but doesn't navigate the user
 * @param orderId - The order ID to create payment link for
 * @returns Promise that resolves when the request completes
 */
export async function createPaymobPaymentLinkInBackground(
    orderId: string | number
): Promise<PaymobPaymentLinkResponse> {
    return createPaymobPaymentLink(orderId)
}
